import { isAxiosError } from 'axios';

export type StandardBillingPayload = {
  error_type?: string | null;
  error?: string | null;
  allowed?: boolean | null;
  reason_code?: string | null;
  message?: string | null;
  metric?: string | null;
  limit?: number | null;
  used?: number | null;
  remaining?: number | null;
  period_ends_at?: string | null;
  warning_code?: string | null;
  warning_message?: string | null;
  status?: string | null;
  reservation_id?: string | null;
  reserved?: unknown;
  expires_at?: string | null;
  event_id?: string | null;
  source?: string | null;
  runtime_action?: string | null;
  event_type?: string | null;
};

const RUNTIME_STOP_TRANSCRIPTION = 'stop_transcription';

export const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null;

export function normalizeStandardBillingPayload(raw: unknown): StandardBillingPayload | null {
  if (!isRecord(raw)) return null;
  return raw as StandardBillingPayload;
}

export function getBillingDisplayMessage(source: unknown): string | null {
  if (source === null || source === undefined) return null;

  if (typeof source === 'string' && source.trim()) return source.trim();

  if (!isRecord(source)) return null;

  const nested = source.billing;
  if (isRecord(nested)) {
    const fromNested =
      nested.message ?? nested.warning_message ?? nested.error ?? source.message ?? source.error;
    if (typeof fromNested === 'string' && fromNested.trim()) return fromNested.trim();
  }

  const direct = source.message ?? source.warning_message ?? source.error;
  if (typeof direct === 'string' && direct.trim()) return direct.trim();

  return null;
}

export function isBillingBlockResponseBody(body: unknown): body is StandardBillingPayload {
  const p = normalizeStandardBillingPayload(body);
  if (!p) return false;
  if (p.error_type === 'billing') return true;
  if (p.allowed === false && typeof p.reason_code === 'string') return true;
  return false;
}

export function getBillingBlockFromAxiosError(error: unknown): StandardBillingPayload | null {
  if (!isAxiosError(error)) return null;
  const status = error.response?.status;
  if (status !== 402) return null;
  return normalizeStandardBillingPayload(error.response?.data) ?? null;
}

export function isBillingUiDisabledError(error: unknown): boolean {
  if (!isAxiosError(error)) return false;
  if (error.response?.status !== 404) return false;
  const data = error.response?.data;
  if (!isRecord(data)) return false;
  const detail = data.detail;
  return typeof detail === 'string' && detail.includes('Billing UI is disabled');
}

export function shouldStopTranscriptionFromBilling(
  billing: StandardBillingPayload | null | undefined,
): boolean {
  if (!billing?.runtime_action) return false;
  return billing.runtime_action === RUNTIME_STOP_TRANSCRIPTION;
}

export type ParsedBillingWsStatus = {
  agent?: string;
  message: string | null;
  billing: StandardBillingPayload | null;
};

export function parseBillingFromChatStatusPayload(payload: unknown): ParsedBillingWsStatus | null {
  if (!isRecord(payload)) return null;
  const agent = typeof payload.agent === 'string' ? payload.agent : undefined;
  if (agent !== 'Billing') return null;
  const nested = normalizeStandardBillingPayload(payload.billing);
  const billing: StandardBillingPayload = {
    ...(nested ?? {}),
    allowed:
      nested?.allowed !== undefined && nested?.allowed !== null
        ? nested.allowed
        : typeof payload.allowed === 'boolean'
          ? payload.allowed
          : (nested?.allowed ?? null),
    reason_code:
      nested?.reason_code ?? (typeof payload.reason_code === 'string' ? payload.reason_code : null),
    metric: nested?.metric ?? (typeof payload.metric === 'string' ? payload.metric : null),
    remaining:
      typeof payload.remaining === 'number'
        ? payload.remaining
        : typeof nested?.remaining === 'number'
          ? nested.remaining
          : null,
  };
  const message = getBillingDisplayMessage(payload) ?? getBillingDisplayMessage({ billing });
  return { agent, message, billing };
}
